import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';

export interface IChartLegendItem {
    splitId: string;
    color: string;
    state: 'active' | 'inactive';
}

interface IChartsSplit {
    chartInstanceId: string;
    // splitName: string;
    splitId: string;
    color: string;
}

@Injectable({
    providedIn: 'root',
})
export class CommonLegendService {
    private registeredLegendItems: IChartLegendItem[][] = [];
    public registeredLegendItems$ = new BehaviorSubject<IChartLegendItem[][]>(
        []
    );

    private registeredChartSplits: IChartsSplit[] = [];

    public toggleLegend$ = new Subject<string>();
    public isLoading$ = new BehaviorSubject<boolean>(true);

    registerChartSeries(
        chartInstanceId: string,
        splits: { splitName: string; splitId: string; color: string }[]
    ) {
        // remove all chartsSplits with the same chartInstanceId
        this.registeredChartSplits = this.registeredChartSplits.filter(
            (chartSplit) => chartSplit.chartInstanceId !== chartInstanceId
        );
        // add new ChartSplits
        this.registeredChartSplits = this.registeredChartSplits.concat(
            splits.map((split) => ({
                ...split,
                chartInstanceId,
                state: 'active',
            }))
        );

        this.registeredLegendItems = this.aggregateChartSplits(
            this.registeredChartSplits
        );
        this.registeredLegendItems$.next(this.registeredLegendItems);
    }

    /**
     * Clears the series of a specific Chart instance.
     *
     * @param chartInstanceId - The ID of the chart instance to clear.
     *
     * @remarks
     * This method removes all splits associated with the specified chart instance from the registeredChartSplits array.
     * It then aggregates the remaining splits and updates the registeredLegendItems array and the registeredLegendItems$ observable.
     */
    clearChartSeries(chartInstanceId: string) {
        this.registeredChartSplits = this.registeredChartSplits.filter(
            (chartSplit) => chartSplit.chartInstanceId !== chartInstanceId
        );

        this.registeredLegendItems = this.aggregateChartSplits(
            this.registeredChartSplits
        );
        this.registeredLegendItems$.next(this.registeredLegendItems);
    }

    /**
     * Aggregates registeredChartSplits by splitId and color.
     *
     * @returns An array of objects, each with the shape {chartInstances: number, splitId: string, color: string}.
     */
    aggregateChartSplits(chartSplits: IChartsSplit[]): IChartLegendItem[][] {
        // Create an empty object to store the aggregated data
        const aggregatedData: {
            [key: string]: {
                chartInstances: number;
                splitId: string;
                color: string;
                state: 'active' | 'inactive';
            };
        } = {};

        // Iterate over each item in registeredChartSplits
        for (const split of chartSplits) {
            // Create a unique key for each combination of splitId and color
            const key = `${split.splitId}-${split.color}`;

            // If this key already exists in the aggregatedData object, increment the count
            // Otherwise, add a new object to the aggregatedData object with a count of 1
            if (aggregatedData[key]) {
                aggregatedData[key].chartInstances++;
            } else {
                aggregatedData[key] = {
                    chartInstances: 1,
                    splitId: split.splitId,
                    color: split.color,
                    state: 'active',
                };
            }
        }
        // reorder aggregatedData by splitId
        const orderedAggregatedData = Object.keys(aggregatedData)
            .sort()
            .reduce((obj: any, key) => {
                obj[key] = aggregatedData[key];
                return obj;
            }, {});

        const arr: IChartLegendItem[][] = [];
        arr.push(Object.values(orderedAggregatedData));
        // Convert the aggregatedData object to an array and return it
        return arr;
    }

    getEvent() {
        return this.registeredLegendItems$.asObservable();
    }

    requestToggleLegend(seriesId: string) {
        this.registeredLegendItems.map((items) => {
            // change state of series
            items.map((series) => {
                if (series.splitId === seriesId) {
                    series.state =
                        series.state === 'active' ? 'inactive' : 'active';
                }
            });
        });
        // emit new state
        this.registeredLegendItems$.next(this.registeredLegendItems);
        // emit event
        this.toggleLegend$.next(seriesId);
    }

    showLoading() {
        this.isLoading$.next(true);
    }

    hideLoading() {
        this.isLoading$.next(false);
    }
}
